import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "../../utils/common/cn";
import React from "react";
import type { NavItem } from "./StepsNavigation";

export interface StepsFooterProps {
  /** Nav items (required in route mode) */
  navItems?: NavItem[];
  /** Base path for route matching in route mode */
  basePath?: string;
  /** Current flat step index (state mode) */
  currentStepIndex?: number;
  /** Total number of flat steps (state mode) */
  totalSteps?: number;
  /** Parent steps and their starting flat index (state mode) */
  parentSteps?: Array<{ label: string; startIndex: number; path?: string }>;
  /** Callback to change step — receives the new flat index (state mode) */
  onStepChange?: (index: number) => void;
  /** Additional className for the footer */
  className?: string;
  /** Whether to hide the first step */
  isFirstStepHidden?: boolean;
}

export function StepsFooter({
  navItems,
  basePath,
  currentStepIndex,
  parentSteps,
  onStepChange,
  className,
  isFirstStepHidden = false,
}: StepsFooterProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const useStateNavigation =
    currentStepIndex !== undefined && typeof onStepChange === "function";

  const pathMatches = (path?: string) => {
    if (!path) return false;
    if (basePath && path === basePath) {
      return (
        location.pathname === basePath || location.pathname === `${basePath}/`
      );
    }
    return (
      location.pathname === path ||
      location.pathname === `${path}/` ||
      location.pathname.startsWith(path)
    );
  };

  const parentEntries =
    parentSteps && parentSteps.length > 0
      ? parentSteps
      : (navItems || []).reduce<Array<{ label: string; startIndex: number; path?: string }>>(
          (acc, item) => {
            const prev = acc[acc.length - 1];
            const startIndex = prev
              ? prev.startIndex +
                1 +
                ((navItems || [])[acc.length - 1]?.children?.length || 0)
              : 0;
            acc.push({ label: item.label, startIndex, path: item.path });
            return acc;
          },
          []
        );

  const safeCurrentStepIndex = useStateNavigation ? (currentStepIndex as number) : 0;

  const currentParentPosition = useStateNavigation
    ? parentEntries.reduce((acc, step, idx) => {
        return step.startIndex <= safeCurrentStepIndex ? idx : acc;
      }, 0)
    : (navItems || []).findIndex((item) => {
        const parentMatch = pathMatches(item.path);
        const childMatch =
          item.children?.some(
            (child) => pathMatches(child.path)
          ) || false;
        return parentMatch || childMatch;
      });

  const safeParentPosition =
    currentParentPosition >= 0 ? currentParentPosition : 0;

  const currentParentStep = parentEntries[safeParentPosition] || null;
  const previousParentStep = parentEntries[safeParentPosition - 1] || null;
  const nextParentStep = parentEntries[safeParentPosition + 1] || null;
  const isOnParentStart = currentParentStep
    ? useStateNavigation
      ? safeCurrentStepIndex === currentParentStep.startIndex
      : !!currentParentStep.path &&
        (location.pathname === currentParentStep.path ||
          location.pathname === `${currentParentStep.path}/`)
    : false;
  const backTargetParent = isOnParentStart ? previousParentStep : currentParentStep;
  const isFirstParentStep = backTargetParent === null;
  const isLastParentStep = nextParentStep === null;

  const totalStepsDisplay = Math.max(parentEntries.length, 1);
  const currentStepDisplayIndex = safeParentPosition;

  const handleBack = () => {
    if (backTargetParent) {
      if (useStateNavigation) {
        onStepChange!(backTargetParent.startIndex);
      } else {
        navigate(backTargetParent.path || basePath || "/promotioniq");
      }
    }
  };

  const handleNext = () => {
    if (nextParentStep) {
      if (useStateNavigation) {
        onStepChange!(nextParentStep.startIndex);
      } else {
        navigate(nextParentStep.path || basePath || "/promotioniq");
      }
    }
  };

  return (
    <footer
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 px-6 py-2",
        className
      )}
      style={{ backgroundColor: "#191e27" }}
    >
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <span className="text-sm text-muted-foreground">
          Step {currentStepDisplayIndex + 1} of {totalStepsDisplay}
        </span>
        {!(isFirstStepHidden && safeParentPosition === 0 && isOnParentStart) && (
        <div className="flex gap-4">
          {/* Back button — hidden on first step */}
          {!isFirstParentStep && (
            <button
              onClick={handleBack}
              className="px-6 py-2 border border-border font-medium min-w-[100px] hover:border-primary"
            >
              {backTargetParent?.label || "Back"}
            </button>
          )}
          {/* Next / Complete button */}
          {isLastParentStep ? (
            <div className="px-6 py-2 font-medium min-w-[100px]">
             
            </div>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-primary text-primary-foreground font-medium min-w-[100px] hover:bg-primary/90"
            >
              {nextParentStep?.label || "Next"}
            </button>
          )}
        </div>
        )}
      </div>
    </footer>
  );
}

export default StepsFooter;
